import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { JTable } from '@atoms/JTable';
import { Box, Stack } from '@mui/material';
import { ISearchFilter } from '@models/dto/searchCriteria.dto';
import { JTablePagination } from '@atoms/JTablePagination';
import { KEY_ORDER } from '@models/key-order';
import TableColumn from '@models/table-column';
import { useGetCandidates } from '@candidates/queries/useGetCandidates';
import CandidateRow from '../CandidateRow';
import { JAutocomplete } from '@components/molucules/JAutocomplete';
import { FilterCriteriaFields } from '@models/filter-criteria-fields';
import IOption from '@models/option';
import { useDebouncedCallback } from 'use-debounce';
import { useCities } from '@/queries/useCities';
import { JSelect } from '@atoms/JSelect';
import { SearchTextField } from '@atoms/SearchTextField';

const TABLE_COLS: TableColumn[] = [
  { name: 'FIRSTNAME', orderable: true, sortingField: 'candidate.firstname' },
  {
    name: 'LASTNAME',
    orderable: true,
    sortingField: 'candidate.lastname',
  },
  {
    name: 'EMAIL',
    orderable: true,
    sortingField: 'candidate.email',
  },
  {
    name: 'PHONE_NUMBER',
    orderable: true,
    sortingField: 'candidate.phoneNumber',
  },
  {
    name: 'CITY_ZIP_CODE',
    orderable: true,
    sortingField: 'candidate.city',
  },
  {
    name: 'CV_VIDEO_STATUS',
    orderable: true,
    sortingField: 'candidate.cvvideo.status',
  },

  {
    name: 'PROFILE_STATUS',
    orderable: true,
    sortingField: 'candidate.status',
  },
];

export const PER_PAGE = 20;

const CandidatesList: React.FC = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState<number>();
  const [order, setOrder] = useState<{
    order: KEY_ORDER;
    orderBy: string;
  }>();
  const [searchFilter, setSearchFilter] = useState<ISearchFilter[]>([]);
  const [cityInputValue, setCityInputValue] = useState<string>('');

  const { data } = useGetCandidates(
    {
      page,
      limit: PER_PAGE,
      order: order?.order,
      orderBy: order?.orderBy,
    },
    {
      searchCriteria: searchFilter,
    },
  );
  const { data: cities } = useCities(10, 1, cityInputValue);
  const rows = data?.data.data;
  const meta = data?.data.meta;
  const pagesCount = data?.data.meta.itemCount;
  const isNotFound = rows?.length === 0;

  const handleColumnClick = (column: string) => {
    if (!order) return;
    if (order.orderBy === column) {
      // If user clicks on the same column, switch the order
      setOrder((prevOrder) => ({
        order:
          prevOrder?.order === KEY_ORDER.ASC ? KEY_ORDER.DESC : KEY_ORDER.ASC,
        orderBy: column,
      }));
    } else {
      // If user clicks on a different column, update order field and use default order
      setOrder({
        order: KEY_ORDER.ASC,
        orderBy: column,
      });
    }
  };

  const appendSearchFilter = (
    field: FilterCriteriaFields,
    value: string,
  ): void => {
    const existed = searchFilter?.some((item) => item.field === field);

    if (!existed) setSearchFilter([...searchFilter, { field, value }]);
    if (value === '')
      setSearchFilter((prevValue) =>
        prevValue.filter((item) => item.field !== field),
      );
    else
      setSearchFilter((prevValue) =>
        prevValue.map((item) =>
          item.field === field ? { field, value } : item,
        ),
      );
  };
  const debouncedAppendSearchFilter = useDebouncedCallback(
    appendSearchFilter,
    1000,
  );
  const debouncedCity = useDebouncedCallback((value) => {
    setCityInputValue(value);
  }, 1000);

  const citiesOptions = cities
    ? cities.data.data.map(({ id, name, zipCode }) => {
        return { value: id, label: `${name} / ${zipCode}` };
      })
    : [];
  return (
    <>
      <Stack marginY={3} direction="row" gap={2}>
        <JSelect
          value={
            searchFilter.find(
              (item) => item.field === FilterCriteriaFields.CANDIDATE_STATUS,
            )?.value ?? ''
          }
          options={[
            { label: 'Tous', value: '' },
            { label: 'Active', value: 'true' },
            { label: 'Inactive', value: 'false' },
          ]}
          size="small"
          label="Statut"
          onChange={(event) =>
            appendSearchFilter(
              FilterCriteriaFields.CANDIDATE_STATUS,
              event.target.value as string,
            )
          }
        />

        <SearchTextField
          label={t('FULL_NAME') ?? undefined}
          onChange={(value) => {
            debouncedAppendSearchFilter(
              FilterCriteriaFields.USER_FULLNAME,
              value,
            );
          }}
        />

        <JAutocomplete
          label={t('CITY_ZIP_CODE')}
          options={citiesOptions}
          onChange={(ev, newValue) =>
            appendSearchFilter(
              FilterCriteriaFields.USER_CITY_ID,
              (newValue as IOption)?.value || '',
            )
          }
          onTextChange={(val) => debouncedCity(val)}
        />

        <SearchTextField
          label={t('EMAIL') ?? undefined}
          onChange={(value) => {
            debouncedAppendSearchFilter(FilterCriteriaFields.USER_EMAIL, value);
          }}
        />
        <SearchTextField
          label={t('PHONE_NUMBER') ?? undefined}
          onChange={(value) => {
            debouncedAppendSearchFilter(
              FilterCriteriaFields.USER_MOBILEPHONE,
              value,
            );
          }}
        />
      </Stack>

      <JTable
        isNotFound={isNotFound}
        columns={TABLE_COLS}
        notFoundLabel={t('NO_CANDIDAT_FOUND') ?? ''}
        total={pagesCount ?? 0}
        perpage={PER_PAGE}
        nbpages={pagesCount ?? 0}
        onChangeOrder={(value) => handleColumnClick(value)}
      >
        {rows?.map((row) => (
          <CandidateRow row={row} key={row.id} />
        ))}
      </JTable>

      {meta && (
        <Box mt={2}>
          <JTablePagination
            onPageChange={(value) => {
              setPage(value);
            }}
            meta={meta}
          />
        </Box>
      )}
    </>
  );
};

export default CandidatesList;
